import * as M3 from './model3d.ts';
/* TREES AND SHRUBS HAVE STRUCTURE (LS-186).
 *
 * A crown used to be three lumpy discs stacked -- under, body, lit -- and a
 * trunk a bar; a forest read as green blobs on sticks. This paints a crown as
 * FOLIAGE: a handful of leaf clusters placed around the crown's centre, each a
 * body disc with a lit cap above and a shadow beneath, rimmed with leaf
 * highlights on the lit side and notched with dark leaf gaps on the other,
 * so the silhouette is lobed and the light has a direction. Species differ
 * in what the clusters are: broadleaf (round clumps), fen willow (the same,
 * with strands hanging from the lower edge), dust scrub (few small clumps on
 * visible twigs), ash snag (no leaves at all: a trunk and bare branches with
 * a lit edge). A trunk has bark, a root flare and a ground shadow.
 *
 * Everything is a pure function of (seed, position), so a tree is the same
 * tree when you walk back to it and the same tree on every frame but for the
 * wind, which is passed in. The painter takes a 2D context, because the
 * canopy is baked to a canvas and the moving crowns are redrawn per frame. */

export type CrownTones = { under: string; body: string; lit: string; rim: string; stem: string; dark?: string };

const hash = (seed: number, a: number, b: number) => {
  let h = (seed ^ Math.imul(a | 0, 374761393) ^ Math.imul(b | 0, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 2246822519) >>> 0;
  return ((h ^ (h >>> 15)) >>> 8) / 16777216;
};

function disc(g: any, cx: number, cy: number, rx: number, ry: number, col: string) {
  g.fillStyle = col;
  const y0 = Math.floor(cy - ry), y1 = Math.ceil(cy + ry);
  for (let y = y0; y <= y1; y++) {
    const v = (y + 0.5 - cy) / ry;
    const s = 1 - v * v;
    if (s <= 0) continue;
    const half = rx * Math.sqrt(s);
    const x0 = Math.round(cx - half), x1 = Math.round(cx + half);
    if (x1 > x0) g.fillRect(x0, y, x1 - x0, 1);
  }
}
function px(g: any, x: number, y: number, col: string) { g.fillStyle = col; g.fillRect(Math.round(x), Math.round(y), 1, 1); }
function seg(g: any, x0: number, y0: number, x1: number, y1: number, w: number, col: string) {
  const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0)));
  g.fillStyle = col;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    g.fillRect(Math.round(x0 + (x1 - x0) * t - w / 2), Math.round(y0 + (y1 - y0) * t - w / 2), Math.max(1, Math.round(w)), Math.max(1, Math.round(w)));
  }
}

/** The leaf clusters of a crown: where they sit and how big they are. Pure. */
/* A CROWN IS A CLOUD OF LOBES IN SPACE (LS-199). Vijay: "let's use the 3d
 * pipeline to make all assets consistent perspective".
 *
 * The lobes used to be scattered on an ELLIPSE -- an angle, a distance, and
 * the whole thing squashed vertically by a number -- which is a flat disc
 * wearing a foreshortening factor, and it is why a tree read as a sticker
 * standing next to a body that now genuinely turns in space. They are placed
 * on a SPHERE now and projected through the camera every asset shares: a lobe
 * further from the viewer sits higher on the screen and is drawn first and
 * darker, a lobe in front of the trunk sits lower and covers it. `squash`
 * survives as the crown's own flatness (a scrub is a pancake, an oak is a
 * ball), not as the projection -- the projection is `DK`, and it is the same
 * one the forager and the rocks use.
 *
 * Each lobe carries its `z` so the painters can order and shade by depth. */
export function clustersOf(cx: number, cy: number, r: number, squash: number, seed: number, kind: string) {
  const n = kind === 'scrub' ? 3 + Math.floor(hash(seed, 1, 1) * 2) : 4 + Math.floor(r / 4) + Math.floor(hash(seed, 2, 2) * 2);
  const out: { x: number; y: number; r: number; k: number; z: number }[] = [];
  // height and depth of the crown's ball, in world units; the vertical result
  // is scaled to the extent the old ellipse had so no tree changes size
  const HKf = 0.62, DKf = 0.5, fit = squash / 0.62;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash(seed, 3, i) * 1.2;
    const el = (hash(seed, 7, i) - 0.42) * 1.5;                  // up and down the ball
    const d = r * (0.25 + hash(seed, 4, i) * 0.45);
    const lx = Math.cos(a) * d * Math.cos(el);
    const lz = Math.sin(a) * d * Math.cos(el);
    const ly = Math.sin(el) * d * 0.9;
    const cr = Math.max(2.5, r * (kind === 'scrub' ? 0.34 : 0.46) * (0.8 + hash(seed, 5, i) * 0.4));
    out.push({ x: cx + lx, y: cy + (-ly * HKf + lz * DKf) * fit, r: cr, k: i, z: lz });
  }
  // the crown's heart, so the middle is never hollow
  if (kind !== 'scrub') out.push({ x: cx, y: cy - r * 0.1, r: r * 0.5, k: n, z: 0 });
  // back to front: the far lobes are laid down first and the near ones cover
  // them, which is the whole of the depth in a crown
  return out.sort((p, q) => p.z - q.z);
}

/** The still part of a crown: shadows, twigs, bodies, leaf notches and, on a
 *  willow, the strands. Baked once. */
export function paintCrownBody(g: any, cx: number, cy: number, r: number, squash: number, seed: number, kind: string, t: CrownTones, blossom: string | null = null) {
  if (kind === 'snag') { paintSnag(g, cx, cy, r, seed, t); return; }
  const cl = clustersOf(cx, cy, r, squash, seed, kind);
  const dark = t.dark || t.under;
  // scrub: the clumps sit on twigs that fork from the crown's centre
  if (kind === 'scrub') for (const c of cl) seg(g, cx, cy + r * 0.5, c.x, c.y, 1, t.stem);
  // shadows beneath every cluster first, then bodies: the order is what makes
  // a lobe read as sitting on the one behind it
  for (const c of cl) disc(g, c.x + 1, c.y + 1.6, c.r * 1.05, c.r * 0.9 * squash, dark);
  // ...and a lobe at the BACK of the crown is in the crown's own shade
  for (const c of cl) disc(g, c.x, c.y, c.r, c.r * 0.92 * squash, c.z < -r * 0.12 ? dark : t.body);
  for (const c of cl) {
    const m = 2 + Math.floor(c.r / 2);
    for (let i = 0; i < m; i++) {
      const b = 0.4 + i * (1.6 / m) + hash(seed, 9 + c.k, i) * 0.3;
      px(g, c.x + Math.cos(b) * (c.r - 0.8), c.y + Math.sin(b) * (c.r - 0.8) * squash, dark);
    }
  }
  /* A TREE IN BLOSSOM IS PINK (2026-09-05, against the reference tree sheet, where the bloom country's tree is a
   * canopy of flower with green showing through it). This laid one or two petals per lobe on a green crown, so from
   * any distance the bloom read exactly like the heath. The flower covers the sunward half of every lobe. */
  if (blossom) for (const c of cl) {
    const m = 3 + Math.floor(c.r * 1.6);
    for (let i = 0; i < m; i++) {
      const a = -2.9 + hash(seed, 16 + c.k, i) * 3.0;
      const rad = 0.25 + hash(seed, 26 + c.k, i) * 0.75;
      px(g, c.x + Math.cos(a) * c.r * rad, c.y + Math.sin(a) * c.r * rad * squash - 0.5, blossom);
    }
  }
  if (kind === 'willow') {
    /* WITHIES HANG WITH WEIGHT (2026-09-05, against the reference tree sheet): a straight vertical line reads as
     * rain. A strand is thick where it leaves the crown, thin at its tip, and drifts a little as it falls. */
    const n = 3 + Math.floor(r / 3);
    for (let i = 0; i < n; i++) {
      const x = cx - r * 0.8 + (i / (n - 1)) * r * 1.6 + hash(seed, 12, i) * 2;
      const y0 = cy + r * 0.55 * squash, len = 4 + hash(seed, 13, i) * (r * 0.8);
      const drift = (hash(seed, 14, i) - 0.5) * 2.2;
      seg(g, x, y0, x + drift * 0.35, y0 + len * 0.55, 1, t.body);
      seg(g, x + drift * 0.35, y0 + len * 0.55, x + drift, y0 + len, 1, dark);
      px(g, x + drift, y0 + len, t.lit);
      if (len > 7) px(g, x - 1, y0 + 1, dark);   // the shaded side where it leaves the crown
    }
  }
}

/** The moving part of a crown: the lit caps, the rim highlights, blossoms,
 *  and a willow's strand tips. Redrawn per frame with the wind's `sway`. */
export function paintCrownLit(g: any, cx: number, cy: number, r: number, squash: number, seed: number, kind: string, t: CrownTones, sway = 0, blossom: string | null = null) {
  if (kind === 'snag') return;
  const cl = clustersOf(cx, cy, r, squash, seed, kind);
  // the bodies ride the wind too, half as far as the lit caps: the crown moves
  // as a mass, and the live pass carries the wood's own green with it
  if (t.body) for (const c of cl) disc(g, c.x + sway * 0.5, c.y, c.r, c.r * 0.92 * squash, c.z < -r * 0.12 ? (t.dark || t.under) : t.body);
  for (const c of cl) {
    // the sky lights the lobes that face it: the ones toward the viewer and
    // the top of the ball, not the ones tucked behind
    if (c.z < -r * 0.12) continue;
    disc(g, c.x - 0.6 + sway, c.y - c.r * 0.45, c.r * 0.72, c.r * 0.5 * squash, t.lit);
    const m = 1 + Math.floor(c.r / 3);
    for (let i = 0; i < m; i++) {
      const a = -2.4 + i * (1.6 / m) + hash(seed, 6 + c.k, i) * 0.3;
      px(g, c.x + Math.cos(a) * (c.r - 0.5) + sway, c.y + Math.sin(a) * (c.r - 0.5) * squash, t.rim);
    }
  }
  if (blossom) for (const c of cl) for (let i = 0; i < 1 + Math.floor(c.r / 2); i++) {
    const a = -2.6 + hash(seed, 16 + c.k, i) * 2.2;
    px(g, c.x + Math.cos(a) * c.r * 0.6 + sway, c.y + Math.sin(a) * c.r * 0.5 * squash - 0.5, blossom);
  }
  if (kind === 'willow' && sway) {
    const n = 3 + Math.floor(r / 3);
    for (let i = 0; i < n; i++) {
      const x = cx - r * 0.8 + (i / (n - 1)) * r * 1.6 + hash(seed, 12, i) * 2;
      const y0 = cy + r * 0.55 * squash, len = 4 + hash(seed, 13, i) * (r * 0.8);
      const drift = sway * (0.5 + hash(seed, 14, i) * 0.8);
      seg(g, x, y0 + len * 0.5, x + drift, y0 + len, 1, t.body);
      px(g, x + drift, y0 + len, t.lit);
    }
  }
}

/** Paint one whole crown of foliage at (cx, cy) radius r: the still part and
 *  the lit part together. */
export function paintCrown(g: any, cx: number, cy: number, r: number, squash: number, seed: number, kind: string, t: CrownTones, sway = 0, blossom: string | null = null) {
  paintCrownBody(g, cx, cy, r, squash, seed, kind, t);
  paintCrownLit(g, cx, cy, r, squash, seed, kind, t, sway, blossom);
}

/** A dead tree: a trunk and bare branches, two levels, a lit edge on top. */
export function paintSnag(g: any, cx: number, cy: number, r: number, seed: number, t: CrownTones) {
  const base = [cx, cy + r * 0.9];
  const top = [cx + (hash(seed, 20, 0) - 0.5) * 3, cy - r * 0.4];
  seg(g, base[0], base[1], top[0], top[1], 2.4, t.stem);
  const n = 3 + Math.floor(hash(seed, 21, 0) * 3);
  for (let i = 0; i < n; i++) {
    const u = 0.3 + (i / n) * 0.65;
    const bx = base[0] + (top[0] - base[0]) * u, by = base[1] + (top[1] - base[1]) * u;
    const side = i % 2 ? 1 : -1;
    const a = -Math.PI / 2 + side * (0.6 + hash(seed, 22, i) * 0.6);
    const L = r * (0.5 + hash(seed, 23, i) * 0.5);
    const ex = bx + Math.cos(a) * L, ey = by + Math.sin(a) * L;
    seg(g, bx, by, ex, ey, 1.4, t.stem);
    // a twig off each branch
    const a2 = a + side * (0.5 + hash(seed, 24, i) * 0.5);
    seg(g, ex, ey, ex + Math.cos(a2) * L * 0.45, ey + Math.sin(a2) * L * 0.45, 1, t.stem);
    px(g, ex, ey - 1, t.rim);
  }
  px(g, top[0], top[1] - 1, t.rim);
}

/** A trunk under a crown: bark, a root flare, a ground shadow. */
export function paintTrunk(g: any, x: number, yTop: number, h: number, seed: number, t: CrownTones, w = 3) {
  /* A TRUNK IS A CYLINDER (LS-199). It was a flat bar of stem colour with one
   * dark pixel down its left edge, which at three pixels wide is a plank --
   * and a plank standing in the dirt is the shape a player put an arrow on
   * ("what are these random ledges..."). Built as a rod through the one
   * camera it is round: lit down the sunward side, shaded on the other, with
   * a root flare spreading where it meets the ground and a shadow pooled on
   * the near side of its foot. The crown above is unchanged. */
  const dark = t.dark || t.under;
  const lit = t.lit || t.body || t.stem;
  const put = (px2: number, py: number, c: string) => { g.fillStyle = c; g.fillRect(px2, py, 1, 1); };
  const skin = { base: t.stem, lit: mix(t.stem, lit, 0.35), dark };
  const foot = yTop + h;
  /* The trunk's shadow is TIGHT. The baked canopy is drawn over the bodies,
   * so anything this pass lays below the trunk's foot is laid over whoever is
   * standing there -- and a pool of shadow spreading a few pixels into the
   * tile below is exactly the "covered up by something that should be below
   * it" a player reported. It stays under the trunk. */
  M3.groundShadow([2.4, 0, 1.6], w * 1.15, Math.round(x), Math.round(foot) - 1, put, 'rgba(10,8,16,0.30)');   // cast down and to the right, with the world's sun
  /* ...AND IT TAPERS INTO ITS ROOTS (2026-09-05, against the reference tree sheet). One rod of one radius is a post:
   * a trunk is thicker at the foot than at the crown, and where it meets the ground it spreads into two or three
   * roots. Three solids and two short rods, and the tree stands in the dirt rather than on it. */
  const x0 = w * 2 + 4, rFoot = Math.max(1.4, w / 2 + 0.5), rTop = Math.max(1.0, w / 2 - 0.35);
  const sheet = M3.renderScene(Math.ceil(w * 4 + 8), Math.ceil(h + 8), [
    { kind: 'ball', at: [x0, 1.2, 0], r: w * 0.95 + 1, rz: w * 0.7, ry: 1.6, skin },
    { kind: 'rod', a: [x0 - w * 1.15, 0.9, 0.4], b: [x0 - w * 0.2, 2.4, 0], r: 0.9, skin },   // roots, spreading
    { kind: 'rod', a: [x0 + w * 1.15, 0.9, 0.4], b: [x0 + w * 0.2, 2.4, 0], r: 0.9, skin },
    { kind: 'rod', a: [x0, 1, 0], b: [x0, h * 0.5, 0], r: rFoot, skin },
    { kind: 'rod', a: [x0, h * 0.5, 0], b: [x0, h - 0.5, 0], r: rTop, skin },
  ], 0, h + 1, undefined);
  M3.blit(sheet, (sx: number, sy: number, c: string) => put(Math.round(x) - (w * 2 + 4) + sx, Math.round(foot) - (h + 1) + sy, c));
  // bark, on the shaded half so it reads as texture and not as a stripe
  for (let i = 0; i < h; i += 3) if (hash(seed, 30, i) > 0.5) px(g, x - w / 2 + 0.5, yTop + i + 1, dark);
}

/** Mix two hex tones; the trunk needs a lit side and the crowns carry only
 *  three inks. */
function mix(a: string, b: string, k: number): string {
  const pa = /^#?([0-9a-f]{6})$/i.exec(a.trim()), pb = /^#?([0-9a-f]{6})$/i.exec(b.trim());
  if (!pa || !pb) return a;
  const va = parseInt(pa[1], 16), vb = parseInt(pb[1], 16);
  const ch = (sh: number) => Math.round(((va >> sh) & 255) + (((vb >> sh) & 255) - ((va >> sh) & 255)) * k);
  return '#' + ((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1);
}

/** A shrub: a low crown with a couple of stems showing and, in bloom, berries. */
export function paintShrub(g: any, cx: number, cy: number, r: number, seed: number, kind: string, t: CrownTones, sway = 0, berries: string | null = null) {
  seg(g, cx - 1, cy + r * 0.4, cx - 2, cy + r * 0.9, 1, t.stem);
  seg(g, cx + 1, cy + r * 0.4, cx + 2.5, cy + r * 0.9, 1, t.stem);
  paintCrown(g, cx, cy, r, 0.85, seed, kind === 'snag' ? 'scrub' : kind, t, sway);
  if (berries) for (let i = 0; i < 2 + Math.floor(r / 3); i++) px(g, cx + (hash(seed, 40, i) - 0.5) * r * 1.4, cy + (hash(seed, 41, i) - 0.6) * r * 0.9, berries);
}
